import { type IDeferred, create } from '@/utils/deferred';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface Task<TaskResult = any> {
    key: string;
    fn: () => Promise<TaskResult>;
}

export const QueueService = Symbol('QueueService');


export interface QueueService {
    initQueue(option:QueueOptions): void;
    push(task: Task): Promise<any>;
    dispose(): void;
    execute(): void;
    clearTasks(): void;
}

export interface QueueOptions {
    maxRetryCount?: number;
    concurrency: number;
    delay?: number;
}


@injectable()
export class QueueServiceImpl implements QueueService {
    tasks: Task[] = [];
    promises: Map<string, IDeferred> = new Map();
    taskExecutedCount: Map<string, number> = new Map();

    /**
     * 是否有正在执行的任务的标记
     */
    processingTaskCount: number = 0;

    protected options: { maxRetryCount: number; concurrency: number; delay: number } = { concurrency: 1, maxRetryCount: 3, delay: 0 };

    constructor() {
    }

    initQueue(options?: QueueOptions) {
        this.options = {
            ...this.options,
            ...options,
        };
    }

    push<T>(task: Task<T>) {
        if (this.promises.has(task.key)) {
            const executedCount = this.taskExecutedCount.get(task.key) || 0;
            if (!this.promises.get(task.key)?.completed && executedCount < this.options.maxRetryCount) {
                this.tasks.push(task);
                this.execute();
            }
            return this.promises.get(task.key)?.promise as Promise<T>;
        }
        this.tasks.push(task);
        const deferred = this.promises.get(task.key) ||create<T>();
        this.promises.set(task.key, deferred);
        this.execute();
        return deferred.promise;
    }

    execute() {
        if (this.processingTaskCount === this.options.concurrency) {
            return;
        }
        const task = this.tasks.shift();
        if (!task) {
            return;
        }
        this.processingTaskCount++;
        let executedCount = this.taskExecutedCount.get(task.key) || 0;
        this.taskExecutedCount.set(task.key, ++executedCount);
        const deferred = this.promises.get(task.key);
        return task.fn().then((_) => {
            this.processingTaskCount--;
            deferred?.resolve(_);
            this.execute();
        }).catch((e) => {
            this.processingTaskCount--;
            if (executedCount < this.options.maxRetryCount) {
                this.push(task);
            } else {
                deferred?.reject(e);
            }
            setTimeout(() => {
                this.execute();
            }, this.options.delay);
        });
    }

    dispose(): void {
        this.tasks = [];
    }

    clearTasks(): void {
        this.tasks = [];
    }
}
